import { useCallback, useEffect, useState } from 'react'

const SESSION_KEY = 'turing.internal.session'

/**
 * Gates the internal dashboard behind an operator passcode held only in
 * sessionStorage, so the tab closing ends the session and nothing persists.
 *
 * NOTE: this is a UI gate for the prototype. Real protection must be enforced
 * server-side (SSO / VPN / signed session) before this dashboard is deployed.
 */
const OPERATOR_PASSCODE = 'turing-ops'

export interface InternalAccess {
  isUnlocked: boolean
  unlock: (passcode: string) => boolean
  lock: () => void
}

export function useInternalAccess(): InternalAccess {
  const [isUnlocked, setIsUnlocked] = useState(false)

  useEffect(() => {
    try {
      setIsUnlocked(window.sessionStorage.getItem(SESSION_KEY) === 'granted')
    } catch {
      setIsUnlocked(false)
    }
  }, [])

  const unlock = useCallback((passcode: string) => {
    if (passcode.trim() !== OPERATOR_PASSCODE) return false
    try {
      window.sessionStorage.setItem(SESSION_KEY, 'granted')
    } catch {
      /* session storage unavailable — keep in-memory access only */
    }
    setIsUnlocked(true)
    return true
  }, [])

  const lock = useCallback(() => {
    try {
      window.sessionStorage.removeItem(SESSION_KEY)
    } catch {
      /* nothing to clear */
    }
    setIsUnlocked(false)
  }, [])

  return { isUnlocked, unlock, lock }
}
